const _excluded = ["options", "values", "onChange", "entity", "entityActions"],
  _excluded2 = ["options", "values", "onChange", "entity", "entityActions"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import PropTypes from 'prop-types';
import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import ReactSelect, { components } from 'react-select';
import Creatable from 'react-select/creatable';
import { Label, FormGroup } from 'reactstrap';
import { kebabCase, isArray } from 'lodash';
import { clone } from 'ramda';
import { v4 as uuid } from 'uuid';
import classnames from 'classnames';
import validator from 'validator';
import nextTick from 'next-tick';
import MenuList from './select/menuList';
import Option from './select/option';
import Control from './select/control';
import MiniLoader from './select/miniLoader';
import Icon from '../containers/icon';
const {
  ClearIndicator,
  DropdownIndicator,
  MultiValue,
  SingleValue,
  Input
} = components;

// Default menu stacks below fixed nav; portaled menus must clear Bootstrap modal (1050).
const SELECT_MENU_Z_INDEX = 1031;
const SELECT_MENU_PORTAL_Z_INDEX = 1060;

// Map react-select's palette to Light/Dark CSS vars so any style path that still
// reads theme.colors (not only our styles{} overrides) stays readable in dark mode.
const selectTheme = theme => _objectSpread(_objectSpread({}, theme), {}, {
  colors: _objectSpread(_objectSpread({}, theme.colors), {}, {
    primary: 'var(--theme-brand-primary, #0d8f6e)',
    primary25: 'var(--theme-nav-hover-bg, rgba(0, 0, 0, 0.06))',
    primary50: 'var(--theme-nav-hover-bg, rgba(0, 0, 0, 0.06))',
    primary75: 'var(--theme-brand-primary, #0d8f6e)',
    neutral0: 'var(--theme-bg-primary, #ffffff)',
    neutral5: 'var(--theme-bg-secondary, #f0f2f5)',
    neutral10: 'var(--theme-bg-secondary, #f0f2f5)',
    neutral20: 'var(--theme-input-border, #E0E0E0)',
    neutral30: 'var(--theme-input-border, #E0E0E0)',
    neutral40: 'var(--theme-text-secondary, #666666)',
    neutral50: 'var(--theme-text-secondary, #666666)',
    neutral60: 'var(--theme-text-secondary, #666666)',
    neutral70: 'var(--theme-text-primary, #333333)',
    neutral80: 'var(--theme-text-primary, #333333)',
    neutral90: 'var(--theme-text-primary, #333333)'
  })
});
const valuesUnchanged = (prevValues, nextValues) => {
  if (prevValues === nextValues) {
    return true;
  }
  if (Array.isArray(prevValues) && Array.isArray(nextValues)) {
    return prevValues.length === nextValues.length && prevValues.every((value, index) => value === nextValues[index]);
  }
  return false;
};
const Select = /*#__PURE__*/React.memo(props => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCache, setIsSelected] = useState([]);
  const prepareOptions = (values = []) => {
    const {
      options,
      labelKey,
      valueKey,
      entity,
      entityActions
    } = props;
    let opts = options || [];
    if ((!options || options.length === 0) && entity && entity.list) {
      opts = entity.list;
    }
    const knownValues = [];
    let localValues = clone(values);
    if (!isArray(localValues)) {
      localValues = [localValues];
    }

    // Get selections from the cache that weren't returned by the query
    const filteredCache = selectedCache.filter(({
      id
    }) => !opts.some(opt => opt.id === id));
    const classNameOptions = [...opts, ...filteredCache].map(opt => {
      if (localValues.includes(opt[valueKey])) {
        knownValues.push(opt);
      }
      return _objectSpread(_objectSpread({}, opt), {}, {
        className: `t-s-opt-${kebabCase(opt[labelKey])}`
      });
    });
    const isValuesString = typeof values === 'string';
    // Is the "other" string a UUID?  If so, the options haven't loaded yet so don't display
    const isStringOtherValue = !isValuesString || !validator.isUUID(values);
    // Add other selections in
    if (!isArray(values) && !!values && !knownValues.length && isStringOtherValue) {
      knownValues.push(isValuesString ? {
        [labelKey]: values,
        [valueKey]: values
      } : values);
    }
    // Add any selected values to the cache
    let valuesForCache = entityActions ? knownValues.filter(({
      id
    }) => !selectedCache.some(selected => selected.id === id)) : [];
    if (valuesForCache.length) {
      valuesForCache = valuesForCache.filter(({
        id
      }) => !knownValues.some(val => val.id === id));
      setIsSelected([...valuesForCache, ...knownValues]);
    }
    return [classNameOptions, knownValues];
  };
  const {
    alwaysOpen,
    allowCreate,
    className: incomingClassName,
    clearable,
    disabled,
    disabledText,
    label,
    searchable,
    labelKey,
    onOpen,
    onTextChange,
    isSearching: isParentSearching,
    entity,
    entityActions,
    maxTextLength,
    multi,
    onChange,
    onFocus,
    placeholder,
    valueKey,
    values,
    verticalPadding,
    includeSelectionsForID,
    dropdownIndicatorGlyph,
    menuPortalTarget,
    menuPosition
  } = props;
  const SelectComponent = allowCreate ? Creatable : ReactSelect;
  const delayedFocus = () => {
    setIsEditing(true);
    return onFocus
    // on next tick, invoke the onFocus method.  MUST be on next tick, or
    // onFocus() does not work.
    ? nextTick(() => onFocus()) : () => {};
  };
  let placeholderText = placeholder;
  if (disabled && disabledText) {
    placeholderText = disabledText;
  }
  const className = classnames({
    't-select': true,
    't-select-single': !multi,
    't-select-multi': multi,
    't-select-other': allowCreate,
    [incomingClassName]: true,
    [`t-select-${kebabCase(label)}`]: !!label,
    'is-disabled': disabled
  });
  const getOptionLabel = option => option[labelKey];
  const getOptionValue = option => option[valueKey];
  const getNewOptionData = (_, optionLabel) => ({
    [valueKey]: uuid(),
    [labelKey]: optionLabel,
    __isNew__: true
  });
  const [preparedOptions, processedValues] = prepareOptions(values);
  const node = useRef();
  const onMenuOpen = () => {
    setIsOpen(true);
    if (onOpen) {
      onOpen();
    }
  };
  const onBlur = () => {
    setIsEditing(false);
    if (isOpen) {
      setIsOpen(false);
    }
  };
  const createOverrideWithClassNames = useCallback((Component, classNames) => function newComponent(valueProps) {
    const allProps = _objectSpread(_objectSpread({}, valueProps), {}, {
      cropWithEllipsis: false,
      className: classNames
    });
    return /*#__PURE__*/React.createElement(Component, allProps);
  }, []);
  const createSelectOverrideWithClassNames = useCallback((Component, classNames) => valueProps => {
    const valueLabel = valueProps.data[labelKey];
    const newClassNames = `t-s-opt-${kebabCase(valueLabel)} ${classNames}`;
    return createOverrideWithClassNames(Component, newClassNames)(valueProps);
  }, [createOverrideWithClassNames, labelKey]);
  const formatCreateLabel = option => `Create "${option}"`;
  const handleClick = event => {
    if (!node.current.contains(event.target)) {
      // outside click
      setIsOpen(false);
    }
  };
  useEffect(() => {
    if (isOpen) {
      global.document.addEventListener('mousedown', handleClick);
    } else {
      global.document.removeEventListener('mousedown', handleClick);
    }
    return () => {
      global.document.removeEventListener('mousedown', handleClick);
    };
  }, [node, isOpen]);
  let currentPageSize = 0;
  let hasNext = null;
  let isSearching = false;
  if (entityActions && entity && entity.indexQuery && entity.indexQuery.paging) {
    currentPageSize = entity.indexQuery.paging.size;
    ({
      hasNext
    } = entity);
    isSearching = !!entity.indexQuery.filters.text;
  }
  const pageNext = entityActions && entity ? (nextSize = 20) => {
    entityActions.serverIndex(_objectSpread(_objectSpread({}, entity.indexQuery), {}, {
      includeSelectionsForID,
      paging: {
        current: 0,
        size: currentPageSize + nextSize
      }
    }));
  } : null;
  const onSelectChange = useDebouncedCallback(text => {
    if (onTextChange) {
      onTextChange(text);
    }
    if (entityActions && entity) {
      const filters = _objectSpread({}, entity.indexQuery && entity.indexQuery.filters || {});
      if (text !== filters.text) {
        filters.text = text;
        entityActions.serverFilter(filters, _objectSpread(_objectSpread({}, entity.indexQuery), {}, {
          paging: {
            current: 0,
            size: currentPageSize
          },
          includeSelectionsForID
        }));
      }
    }
  }, 500);

  // limit the entry text to maxTextLength. The easiest way to do this is by passing a custom
  // function to onInputChange: https://github.com/JedWatson/react-select/issues/2571
  const handleInputChange = inputValue => {
    const limitedValue = inputValue.length <= maxTextLength ? inputValue : inputValue.substr(0, maxTextLength);

    // call the debounced callback for searching
    onSelectChange(limitedValue);
    return limitedValue;
  };
  const padding = processedValues && processedValues.length ? 0 : 6;
  const menuZIndex = menuPortalTarget != null ? SELECT_MENU_PORTAL_Z_INDEX : SELECT_MENU_Z_INDEX;
  const ReactSelectStyles = () => {
    // Option color defaults to `inherit`, which picks up dark-mode body text
    // (#E8E8E8) on react-select's white menu — unreadable. Theme menu + options
    // with CSS vars; fallbacks match `_mode-components.scss` conventions.
    const styles = {
      multiValueLabel: base => _objectSpread(_objectSpread({}, base), {}, {
        whiteSpace: 'normal',
        textOverflow: 'normal'
      }),
      input: base => _objectSpread(_objectSpread({}, base), {}, {
        margin: 0,
        paddingTop: padding,
        paddingBottom: padding,
        color: 'var(--theme-text-primary, #333333)'
      }),
      valueContainer: base => _objectSpread(_objectSpread({}, base), {}, {
        paddingRight: 0,
        overflow: isOpen ? 'hidden' : 'unset',
        backgroundColor: 'transparent',
        color: 'var(--theme-text-primary, #333333)'
      }),
      indicatorsContainer: base => _objectSpread(_objectSpread({}, base), {}, {
        backgroundColor: 'transparent'
      }),
      clearIndicator: base => _objectSpread(_objectSpread({}, base), {}, {
        paddingLeft: 0,
        color: 'var(--theme-text-secondary, #666666)'
      }),
      dropdownIndicator: base => _objectSpread(_objectSpread({}, base), {}, {
        color: 'var(--theme-text-secondary, #666666)'
      }),
      indicatorSeparator: base => _objectSpread(_objectSpread({}, base), {}, {
        backgroundColor: 'var(--theme-input-border, #E0E0E0)'
      }),
      control: (base, state) => {
        const isFocused = state.isFocused && !state.isDisabled;
        // Classic reference always shows the brand-primary border on option
        // picker / search fields, not just on hover/focus (CU-86e3e4rw7).
        const borderColor = state.isDisabled ? 'var(--theme-input-border, #E0E0E0)' : 'var(--theme-brand-primary, #0d8f6e)';
        return _objectSpread(_objectSpread({}, base), {}, {
          backgroundColor: state.isDisabled ? 'var(--theme-bg-secondary, #f0f2f5)' : 'var(--theme-bg-primary, #ffffff)',
          borderColor,
          boxShadow: isFocused ? '0 0 0 0.2rem rgba(var(--theme-brand-primary-rgb, 13, 143, 110), 0.15)' : 'none',
          color: state.isDisabled ? 'var(--theme-text-secondary, #666666)' : 'var(--theme-text-primary, #333333)',
          ':hover': {
            borderColor
          }
        });
      },
      singleValue: (base, state) => _objectSpread(_objectSpread({}, base), {}, {
        backgroundColor: 'transparent',
        color: state.isDisabled ? 'var(--theme-text-secondary, #666666)' : 'var(--theme-text-primary, #333333)'
      }),
      placeholder: base => _objectSpread(_objectSpread({}, base), {}, {
        color: 'var(--theme-text-secondary, #666666)'
      }),
      menu: base => _objectSpread(_objectSpread({}, base), {}, {
        zIndex: menuZIndex,
        backgroundColor: 'var(--theme-bg-primary, #ffffff)',
        color: 'var(--theme-text-primary, #333333)'
      }),
      option: (base, {
        isDisabled,
        isFocused,
        isSelected
      }) => {
        let backgroundColor = 'transparent';
        if (isDisabled) {
          backgroundColor = undefined;
        } else if (isSelected) {
          backgroundColor = 'var(--theme-brand-primary, #0d8f6e)';
        } else if (isFocused) {
          backgroundColor = 'var(--theme-nav-hover-bg, rgba(0, 0, 0, 0.06))';
        }
        let color = 'var(--theme-text-primary, #333333)';
        if (isDisabled) {
          color = 'var(--theme-text-secondary, #666666)';
        } else if (isSelected) {
          color = '#ffffff';
        }
        return _objectSpread(_objectSpread({}, base), {}, {
          backgroundColor,
          color
        });
      },
      noOptionsMessage: base => _objectSpread(_objectSpread({}, base), {}, {
        color: 'var(--theme-text-secondary, #666666)'
      })
    };
    if (menuPortalTarget != null) {
      styles.menuPortal = base => _objectSpread(_objectSpread({}, base), {}, {
        zIndex: SELECT_MENU_PORTAL_Z_INDEX
      });
    }
    return styles;
  };
  useEffect(() => {
    if (entity && entityActions) {
      const query = _objectSpread(_objectSpread({}, entity.indexQuery), {}, {
        includeSelectionsForID
      });
      entityActions.serverIndex(query);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isOpen]);
  const isLoading = (entity || {}).loading || props.isLoading;
  const closeAndChange = opt => {
    if (!multi) {
      setIsOpen(false);
    }
    onChange(opt);
    if (hasNext) {
      pageNext(1);
    }
  };

  /**
   * These components will be re-created each render cycle.  We should keep an eye on
   * the react-select project for an alternative solution.  Currently, this is the reason
   * we need to keep track of focus, have special IE handling, special scroll handling
   * and special logic for controlling the opening and closing of the menu.
   */
  const selectComponents = {
    ClearIndicator: createOverrideWithClassNames(ClearIndicator, 't-select-clear'),
    DropdownIndicator: ddProps => {
      const DropdownIndic = createOverrideWithClassNames(DropdownIndicator, 't-select-arrow-zone');
      return useMemo(() => /*#__PURE__*/React.createElement(DropdownIndic, ddProps, isParentSearching ? /*#__PURE__*/React.createElement(MiniLoader, null) : /*#__PURE__*/React.createElement(Icon, {
        glyph: dropdownIndicatorGlyph
      })), [ddProps]);
    },
    /* eslint-disable-next-line react/no-unstable-nested-components */
    MenuList: menuListProps => /*#__PURE__*/React.createElement(MenuList, _objectSpread(_objectSpread({}, menuListProps), {}, {
      className: "t-select-menu",
      pageNext: pageNext,
      hasNext: hasNext,
      isLoading: isLoading,
      endMessage: props.endMessage
    })),
    Control,
    Option,
    SingleValue: createSelectOverrideWithClassNames(SingleValue, 't-select-value t-select-single-value'),
    MultiValue: createSelectOverrideWithClassNames(MultiValue, 't-select-value t-select-multi-value')
  };

  /**
   * Since IE handles input focus differently we need to defer to the
   * original Input component to prevent loss of user control.  This should be fixed
   * once react-select solves thier components problem.
   */
  const isIE = !!global.document.documentMode;
  if (!isIE) {
    /* eslint-disable-next-line react/no-unstable-nested-components */
    selectComponents.Input = inputProps => /*#__PURE__*/React.createElement(Input, _objectSpread(_objectSpread({}, inputProps), {}, {
      autoFocus: isEditing,
      className: "t-select-input"
    }));
  }
  const localShow = isSearching && !preparedOptions.length && !isLoading;
  const overrideShow = isParentSearching === false && !preparedOptions.length && !isLoading;
  const noOptionsMessage = () => localShow || overrideShow ? 'No results found' : null;
  const resolvedMenuPosition = menuPortalTarget != null ? 'fixed' : menuPosition;
  const select = /*#__PURE__*/React.createElement(SelectComponent, {
    key: "test",
    className: className,
    classNamePrefix: "t-select",
    isClearable: clearable,
    getOptionLabel: getOptionLabel,
    getOptionValue: getOptionValue,
    isDisabled: disabled,
    id: label !== null ? `select-${label}` : 'select',
    onInputChange: handleInputChange,
    menuIsOpen: isOpen || alwaysOpen,
    onMenuOpen: onMenuOpen,
    components: selectComponents,
    isMulti: multi,
    styles: ReactSelectStyles(),
    theme: selectTheme,
    onBlur: onBlur,
    getNewOptionData: getNewOptionData,
    formatCreateLabel: formatCreateLabel,
    onChange: closeAndChange,
    onFilter: false,
    noOptionsMessage: () => noOptionsMessage(),
    onFocus: delayedFocus,
    options: preparedOptions,
    placeholder: placeholderText,
    isSearchable: searchable,
    value: processedValues,
    menuPortalTarget: menuPortalTarget,
    menuPosition: resolvedMenuPosition
  });
  if (verticalPadding) {
    // vertical padding is implemented with FormGroup
    return /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement("div", {
      ref: node
    }, label ? /*#__PURE__*/React.createElement(Label, null, label) : null, select));
  }
  // Form Builder controls do not want extra padding - that is controlled
  // by the format.
  return /*#__PURE__*/React.createElement("div", {
    ref: node
  }, label ? /*#__PURE__*/React.createElement(Label, null, label) : null, select);
}, (prev, next) => {
  const {
      options: prevOpts,
      values: prevValues,
      onChange,
      entity,
      entityActions
    } = prev,
    prevRest = _objectWithoutProperties(prev, _excluded);
  const {
      options: nextOpts,
      values: nextValues,
      onChange: nextOnChange,
      entity: nextEntity,
      entityActions: nextEntityActions
    } = next,
    nextRest = _objectWithoutProperties(next, _excluded2);
  const prevKeys = Object.keys(prevRest);
  const nextKeys = Object.keys(nextRest);

  // If there are a different number of props passed in, re-render
  if (prevKeys.length !== nextKeys.length) {
    return false;
  }
  const prevOptions = prevOpts || entity && entity.list || [];
  const nextOptions = nextOpts || nextEntity && nextEntity.list || [];

  // Basic comparision for any other key, this won't detect function or object changes
  const propsChanged = prevKeys.some(key => prevRest[key] !== nextRest[key]);
  const entityLoadingUnchanged = !entity && !nextEntity || entity && nextEntity && entity.loading === nextEntity.loading;
  return !propsChanged && entityLoadingUnchanged
  // Check to see if the list has changed in size
  && prevOptions.length === nextOptions.length
  // Re-render when the selection changes (id/string or array identity — not just length)
  && valuesUnchanged(prevValues, nextValues);
});
Select.propTypes = {
  alwaysOpen: PropTypes.bool,
  allowCreate: PropTypes.bool,
  className: PropTypes.string,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledText: PropTypes.string,
  onTextChange: PropTypes.func,
  isLoading: PropTypes.bool,
  isSearching: PropTypes.bool,
  entity: PropTypes.shape({
    indexQuery: PropTypes.shape({
      filters: PropTypes.shape({
        text: PropTypes.string
      }),
      paging: PropTypes.shape({
        size: PropTypes.number
      })
    }),
    list: PropTypes.arrayOf(PropTypes.shape({})),
    filters: PropTypes.shape({})
  }),
  entityActions: PropTypes.shape({
    serverFilter: PropTypes.func,
    serverIndex: PropTypes.func
  }),
  includeSelectionsForID: PropTypes.string,
  label: PropTypes.string,
  labelKey: PropTypes.string,
  maxTextLength: PropTypes.number,
  multi: PropTypes.bool,
  onOpen: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  placeholder: PropTypes.string,
  searchable: PropTypes.bool,
  valueKey: PropTypes.string,
  endMessage: PropTypes.string,
  verticalPadding: PropTypes.bool,
  dropdownIndicatorGlyph: PropTypes.string,
  values: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])), PropTypes.oneOfType([PropTypes.string, PropTypes.object])]),
  /* eslint-disable-next-line react/forbid-prop-types -- react-select portal DOM node */
  menuPortalTarget: PropTypes.any,
  menuPosition: PropTypes.oneOf(['absolute', 'fixed'])
};
Select.defaultProps = {
  alwaysOpen: false,
  allowCreate: false,
  dropdownIndicatorGlyph: 'chevron-down',
  className: '',
  clearable: true,
  endMessage: null,
  disabled: false,
  disabledText: '',
  onTextChange: null,
  isLoading: false,
  isSearching: null,
  entity: null,
  entityActions: null,
  includeSelectionsForID: null,
  label: '',
  labelKey: 'name',
  maxTextLength: 2024,
  multi: false,
  onFocus: null,
  onOpen: null,
  options: null,
  placeholder: 'Select...',
  searchable: true,
  valueKey: 'id',
  values: [],
  verticalPadding: true,
  menuPortalTarget: null,
  menuPosition: 'absolute'
};
export default Select;